import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faFilePdf,
  faSpinner,
  faEye,
  faLightbulb,
} from "@fortawesome/free-solid-svg-icons";
import BotAvatar from "./BotAvatar";
import { useTranslation } from "react-i18next";
import { useChatBotStore } from "../../store/ChatBotStore";
import DynamicTable from "./DynamicTable";
import moment from "moment";
import {
  formatChatDateTimes,
  formatStatusLabel,
  getStatusTone,
  statusValuePattern,
} from "./chatbotFormatters";

const MessageBubble = ({ message }) => {
  const { t } = useTranslation("translations");
  const { type, content, timestamp } = message;
  const isUser = type === "user";
  const { fetchPdf, loadingPdf, sendMessage, displayPdfFromBase64 } = useChatBotStore();

  const formatTime = (ts) => {
    return moment(ts).format("HH:mm");
  };

  const renderTextContent = (isError = false) => {
    const rawText = content.text || content.answer;
    if (!rawText) return null;

    const text = formatChatDateTimes(rawText);
    const textClassName = `message-text${isError ? " error" : ""}`;

    const hasStatusContent = /\bstatus/i.test(text);
    if (!hasStatusContent) return <p className={textClassName}>{text}</p>;

    const parts = String(text).split(statusValuePattern);
    return (
      <p className={`${textClassName} message-text-formatted`}>
        {parts.map((part, index) => {
          const isStatus = index % 2 === 1;
          if (!isStatus) return <React.Fragment key={index}>{part}</React.Fragment>;

          const label = formatStatusLabel(part);
          return (
            <span
              key={index}
              className={`status-badge status-${getStatusTone(part)} status-badge-inline`}
              title={label}
            >
              {label}
            </span>
          );
        })}
      </p>
    );
  };

  const renderTableContent = () => {
    if (!content.tableContent) return null;

    const { columns, rows, description } = content.tableContent;
    if (!columns || !rows || rows.length === 0) return null;

    return (
      <div className="message-table-wrapper">
        {description && <p className="table-description">{description}</p>}
        <DynamicTable columns={columns} rows={rows} />
      </div>
    );
  };

  const renderFileContent = () => {
    const hasBase64 = !!content.base64DownloadSource;
    const hasFileId = !!content.fileId;

    if (!hasBase64 && !hasFileId) return null;

    const fileName = content.fileName || "Invoice.pdf";
    const isLoadingThis = loadingPdf.loading && loadingPdf.fileId === content.fileId;

    const handleClick = () => {
      if (hasBase64) {
        displayPdfFromBase64(content.base64DownloadSource, fileName);
      } else if (!isLoadingThis) {
        fetchPdf(content.fileId, fileName);
      }
    };

    return (
      <div className="message-file">
        <div
          className={`file-card ${isLoadingThis ? "loading" : ""}`}
          onClick={handleClick}
          title={t("chatbot.clickToViewPdf")}
        >
          <div className="file-icon">
            {isLoadingThis ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faFilePdf} />
            )}
          </div>
          <div className="file-info">
            <span className="file-name">{fileName}</span>
            <span className="file-action">
              {isLoadingThis ? t("chatbot.loading") : t("chatbot.clickToView")}
            </span>
          </div>
          <div className="file-view-btn">
            <FontAwesomeIcon icon={faEye} />
          </div>
        </div>
      </div>
    );
  };

  const renderQuickReplies = () => {
    if (!content.quickReplies || !Array.isArray(content.quickReplies) || content.quickReplies.length === 0 || isUser) return null;

    const handleQuickReplyClick = (reply) => {
      sendMessage(reply);
    };

    return (
      <div className="message-quick-replies">
        <div className="quick-replies-label">
          <FontAwesomeIcon icon={faLightbulb} />
          <span>{t("chatbot.suggestedReplies")}</span>
        </div>
        <div className="quick-replies-buttons">
          {content.quickReplies.map((reply, index) => (
            <button
              key={index}
              className="quick-reply-btn"
              onClick={() => handleQuickReplyClick(reply)}
            >
              {reply}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const hasTable =
    content.tableContent &&
    content.tableContent.rows &&
    content.tableContent.rows.length > 0;

  return (
    <div
      className={`message-bubble ${isUser ? "user" : "bot"} ${
        hasTable ? "has-table" : ""
      }`}
    >
      <div className="message-avatar">
        {isUser ? <FontAwesomeIcon icon={faUser} /> : <BotAvatar size={30} />}
      </div>
      <div className="message-content">
        <div className="message-body">
          {isUser ? (
            <p className="message-text">{content.text}</p>
          ) : (
            <>
              {content.error ? (
                renderTextContent(true)
              ) : (
                <>
                  {renderTextContent()}
                  {renderTableContent()}
                  {renderFileContent()}
                  {renderQuickReplies()}
                </>
              )}
            </>
          )}
        </div>
        <span className="message-time">{formatTime(timestamp)}</span>
      </div>
    </div>
  );
};

export default MessageBubble;
